export const IMAGE_PROFILES = Object.freeze({
  ingredient: { maxWidth: 480, maxHeight: 480, maxBytes: 60 * 1024 },
  product: { maxWidth: 960, maxHeight: 960, maxBytes: 160 * 1024 },
  promotion: { maxWidth: 960, maxHeight: 960, maxBytes: 160 * 1024 },
  cover: { maxWidth: 1600, maxHeight: 900, maxBytes: 240 * 1024 },
  brand: { maxWidth: 512, maxHeight: 512, maxBytes: 90 * 1024 },
  kiosk: { maxWidth: 960, maxHeight: 720, maxBytes: 160 * 1024 },
});

const MAX_FILE_BYTES = 30 * 1024 * 1024;
const MAX_PIXELS = 60 * 1000 * 1000;
const PASSTHROUGH_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const readDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("No se pudo leer la imagen."));
  reader.readAsDataURL(blob);
});

const loadImage = (image, url) => new Promise((resolve, reject) => {
  const finish = (error) => {
    clearTimeout(timeout);
    image.onload = null;
    image.onerror = null;
    if (error) reject(error);
    else resolve();
  };
  const timeout = setTimeout(() => finish(new Error("La imagen tarda demasiado en abrirse. Prueba con otra foto.")), 30000);
  image.onload = () => finish();
  image.onerror = () => finish(new Error("No se pudo abrir la imagen. Prueba con una foto JPG, PNG o WebP."));
  image.src = url;
});

const encodeCanvas = (canvas, quality) => new Promise((resolve, reject) => {
  // WebP encoding is asynchronous. The browser's PNG fallback preserves alpha.
  canvas.toBlob((blob) => {
    if (!blob?.size) reject(new Error("No se pudo optimizar la imagen. Prueba con otra foto."));
    else resolve(blob);
  }, "image/webp", quality);
});

const fit = (width, height, maxWidth, maxHeight) => {
  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

/** Only called for newly selected files, never for unchanged URLs or saved images. */
export const optimizeImageFile = async (file, { profile = "product", cropBounds } = {}) => {
  if (!(file instanceof Blob) || !file.type.startsWith("image/")) {
    throw new Error("Selecciona un archivo de imagen.");
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error("La imagen supera los 30 MB. Selecciona una foto mas liviana.");
  }
  const limits = IMAGE_PROFILES[profile];
  if (!limits) throw new Error("Perfil de imagen no valido.");

  const url = URL.createObjectURL(file);
  const image = new window.Image();
  const canvas = document.createElement("canvas");
  let cropCanvas;
  try {
    await loadImage(image, url);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    if (!sourceWidth || !sourceHeight || sourceWidth * sourceHeight > MAX_PIXELS) {
      throw new Error("La resolucion de esta imagen no es compatible. Selecciona una foto mas pequena.");
    }

    let source = image;
    let bounds = { cropX: 0, cropY: 0, cropWidth: sourceWidth, cropHeight: sourceHeight };
    if (cropBounds) {
      // Logo background detection must not scan a full-resolution phone photo.
      cropCanvas = document.createElement("canvas");
      const size = fit(sourceWidth, sourceHeight, 1600, 1600);
      cropCanvas.width = size.width;
      cropCanvas.height = size.height;
      const context = cropCanvas.getContext("2d", { willReadFrequently: true });
      if (!context) throw new Error("No se pudo preparar la imagen.");
      context.drawImage(image, 0, 0, size.width, size.height);
      bounds = cropBounds(context.getImageData(0, 0, size.width, size.height).data, size.width, size.height);
      source = cropCanvas;
    }

    let size = fit(bounds.cropWidth, bounds.cropHeight, limits.maxWidth, limits.maxHeight);
    const canUseOriginal = !cropBounds && PASSTHROUGH_TYPES.has(file.type) &&
      size.width === sourceWidth && size.height === sourceHeight && file.size <= limits.maxBytes;
    if (canUseOriginal) return await readDataUrl(file);

    for (let attempt = 0; attempt < 7; attempt += 1) {
      canvas.width = size.width;
      canvas.height = size.height;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("No se pudo preparar la imagen.");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      context.drawImage(source, bounds.cropX, bounds.cropY, bounds.cropWidth, bounds.cropHeight,
        0, 0, size.width, size.height);
      for (const quality of [0.82, 0.72, 0.62]) {
        const blob = await encodeCanvas(canvas, quality);
        if (blob.size <= limits.maxBytes) return await readDataUrl(blob);
        if (blob.type === "image/png") break;
      }
      size = fit(size.width, size.height, Math.max(1, Math.floor(size.width * 0.75)), Math.max(1, Math.floor(size.height * 0.75)));
    }
    throw new Error("No se pudo reducir suficiente la imagen. Prueba con otra foto.");
  } finally {
    URL.revokeObjectURL(url);
    image.src = "";
    canvas.width = canvas.height = 0;
    if (cropCanvas) cropCanvas.width = cropCanvas.height = 0;
  }
};
